import { useEffect } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Alert,
  Badge,
  Tooltip,
  Box,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconTemplate, IconInfoCircle } from "@tabler/icons-react";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import type { Editor } from "@tiptap/core";
import { useCreateInternalGrossCode } from "../../hooks/admin/useInternalGrossCodes";
import { useAuthStore } from "../../store/auth.store";
import { required, scrollToFirstError } from "../../utils/formValidation";

// Available variables for gross code templates
const AVAILABLE_VARIABLES = [
  { name: "specimen_count", description: "Number of specimens", example: "3" },
  {
    name: "specimen_letter",
    description: "Specimen designation",
    example: "A",
  },
  { name: "source", description: "Tissue source", example: "Colon" },
  { name: "subsite", description: "Specific location", example: "Ascending" },
  {
    name: "collection_method",
    description: "How collected",
    example: "Polypectomy",
  },
  { name: "size_cm", description: "Size in centimeters", example: "2.5" },
  { name: "size_mm", description: "Size in millimeters", example: "25" },
  { name: "cassette_count", description: "Number of cassettes", example: "2" },
  { name: "block_count", description: "Number of blocks", example: "2" },
  { name: "color", description: "Tissue color", example: "tan-pink" },
  { name: "consistency", description: "Tissue consistency", example: "soft" },
  {
    name: "fragment_count",
    description: "Number of tissue fragments",
    example: "3",
  },
];

interface SaveAsTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  description: string;
}

interface FormValues {
  code: string;
  description: string;
  scenario_description: string;
  fragment_count: number | null;
}

export default function SaveAsTemplateModal({
  opened,
  onClose,
  description,
}: SaveAsTemplateModalProps) {
  const { profile } = useAuthStore();
  const createMutation = useCreateInternalGrossCode();

  const form = useForm<FormValues>({
    initialValues: {
      code: "",
      description: "",
      scenario_description: "",
      fragment_count: null,
    },
    validate: {
      code: required("Template code is required"),
      description: required("Description is required"),
    },
  });

  // Rich Text Editor for description
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder:
          "Edit your template description. Use {{variable_name}} for dynamic fields...",
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: "",
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      form.setFieldValue("description", html);
    },
  }) as Editor | null;

  // Set editor content when modal opens with the current description
  useEffect(() => {
    if (opened && editor) {
      editor.commands.setContent(description || "");
      form.setFieldValue("description", description || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editor]);

  // Reset editor when modal closes
  useEffect(() => {
    if (!opened && editor) {
      editor.commands.setContent("");
    }
  }, [opened, editor]);

  // Insert variable at cursor position
  const insertVariable = (variableName: string) => {
    if (editor) {
      editor.chain().focus().insertContent(` {{${variableName}}} `).run();
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (!profile?.id) return;

    try {
      await createMutation.mutateAsync({
        code: values.code.trim(),
        description: values.description,
        scenario_description: values.scenario_description.trim(),
        fragment_count: values.fragment_count,
        created_by: profile.id,
      });

      notifications.show({
        title: "Template Submitted",
        message:
          "Your template has been submitted for review. It will be available once approved.",
        color: "green",
      });

      form.reset();
      onClose();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to save template",
        color: "red",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconTemplate size={20} />
          <Text fw={600}>Save as Gross Template</Text>
        </Group>
      }
      centered
      size="xl"
      closeOnClickOutside={false}
    >
      <form
        onSubmit={form.onSubmit(
          (values) => handleSubmit(values),
          () => scrollToFirstError(form)
        )}
        style={{ display: "flex", flexDirection: "column", maxHeight: "70vh" }}
      >
        <Box style={{ flex: 1, overflowY: "auto", paddingBottom: 16 }}>
          <Stack gap="md">
            <TextInput
              label="Template Code"
              placeholder="e.g. COLON-POLYP, SKIN-EXCISION"
              withAsterisk
              {...form.getInputProps("code")}
            />

            <Textarea
              label="Scenario Description"
              description="When should this template be used?"
              placeholder="Describe the scenario when this template applies..."
              minRows={2}
              maxRows={4}
              autosize
              {...form.getInputProps("scenario_description")}
            />

            <NumberInput
              label="Fragment Count"
              description="If set, selecting this code will automatically fill the specimen's fragment count"
              placeholder="e.g. 3"
              min={1}
              max={999}
              {...form.getInputProps("fragment_count")}
            />

            <Paper withBorder p="sm">
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  Available Variables (click to insert)
                </Text>
                <Group gap={6} wrap="wrap">
                  {AVAILABLE_VARIABLES.map((variable) => (
                    <Tooltip
                      key={variable.name}
                      label={`${variable.description} (e.g., "${variable.example}")`}
                      position="top"
                      withArrow
                    >
                      <Badge
                        variant="light"
                        color="indigo"
                        size="md"
                        style={{ cursor: "pointer", fontFamily: "monospace" }}
                        onClick={() => insertVariable(variable.name)}
                      >
                        {`{{${variable.name}}}`}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Stack>
            </Paper>

            <Box>
              <Text size="sm" fw={500} mb={4}>
                Template Description{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <RichTextEditor
                editor={editor}
                styles={{
                  root: {
                    border: form.errors.description
                      ? "1px solid var(--mantine-color-red-6)"
                      : "1px solid var(--mantine-color-gray-4)",
                    borderRadius: "var(--mantine-radius-sm)",
                  },
                  content: {
                    minHeight: 180,
                  },
                  toolbar: {
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                  },
                }}
              >
                <RichTextEditor.Toolbar sticky stickyOffset={60}>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.ColorPicker
                      colors={[
                        "#000000",
                        "#868e96",
                        "#fa5252",
                        "#e64980",
                        "#be4bdb",
                        "#7950f2",
                        "#4c6ef5",
                        "#228be6",
                        "#15aabf",
                        "#12b886",
                        "#40c057",
                        "#82c91e",
                        "#fab005",
                        "#fd7e14",
                      ]}
                    />
                    <RichTextEditor.UnsetColor />
                    <RichTextEditor.Highlight />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
              </RichTextEditor>
              {form.errors.description && (
                <Text size="xs" c="red" mt={4}>
                  {form.errors.description}
                </Text>
              )}
            </Box>

            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              <Text size="sm">
                This template will be submitted for review. Once approved by an
                admin, it will be available in the template dropdown.
              </Text>
            </Alert>
          </Stack>
        </Box>

        <Group
          justify="space-between"
          pt="md"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-3)",
          }}
        >
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={createMutation.isPending}>
            Save Template
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
